import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import {
  ChevronLeft, ChevronRight, CheckCircle2, XCircle, FileText, Palmtree,
  CalendarClock, Coffee, Loader2, UserCheck, Download
} from 'lucide-react';
import axios from 'axios';
import { cn } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  present: { label: 'Présent', icon: CheckCircle2, cellClass: 'bg-emerald-50 border-emerald-400', iconColor: 'text-emerald-500' },
  absent: { label: 'Absent', icon: XCircle, cellClass: 'bg-red-50 border-red-400', iconColor: 'text-red-500' },
  justified: { label: 'Justifié', icon: FileText, cellClass: 'bg-amber-50 border-amber-400', iconColor: 'text-amber-500' },
  holiday: { label: 'Férié', icon: Palmtree, cellClass: 'bg-orange-50 border-orange-400', iconColor: 'text-orange-500' },
  planned: { label: 'Planifié', icon: CalendarClock, cellClass: 'bg-blue-50 border-blue-400', iconColor: 'text-blue-500' },
  rest: { label: 'Repos', icon: Coffee, cellClass: 'bg-gray-50 border-gray-200', iconColor: 'text-gray-400' },
};

const MONTHS_FR = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
const DAYS_SHORT = ['L','M','Me','J','V','S','D'];

function getMonthStr(d) { return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`; }
function getDayOfWeek(ds) { return new Date(ds+'T00:00:00').getDay(); }

const ClientAttendanceCalendar = ({ contractId }) => {
  const [currentMonth, setCurrentMonth] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const monthStr = getMonthStr(currentMonth);

  const fetchAttendance = useCallback(async () => {
    if (!contractId) return;
    setLoading(true); setError(null);
    try {
      const res = await axios.get(`${API}/contracts/${contractId}/attendance?month=${monthStr}`, {});
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur de chargement');
    } finally { setLoading(false); }
  }, [contractId, monthStr]);

  useEffect(() => { fetchAttendance(); }, [fetchAttendance]);

  const handleExportCSV = () => {
    if (!data?.slots?.length) return;
    const rows = [['Poste', 'Véhicules', ...Array.from({ length: data.numDays }, (_, i) => i + 1)]];
    data.slots.forEach(slot => {
      const row = [slot.label, (slot.vehicles || []).join(', ')];
      slot.days.forEach(d => {
        let val = STATUS_CONFIG[d.status]?.label || d.status;
        if (d.runsCompleted) val += ` (${d.runsCompleted}/${d.runsExpected || '?'})`;
        if (d.manual) val += ' [Manuel]';
        row.push(val);
      });
      rows.push(row);
    });
    rows.push([], ['--- KPIs ---']);
    rows.push(['Poste', 'Attendus', 'Présent', 'Absent', 'Justifié', 'Taux %']);
    data.slots.forEach(slot => {
      const k = slot.kpis || {};
      rows.push([slot.label, k.expected||0, k.present||0, k.absent||0, k.justified||0, `${k.presenceRate||0}%`]);
    });
    const csv = rows.map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url;
    a.download = `presence_${contractId}_${monthStr}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5" data-testid="client-attendance-calendar">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="icon" onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth()-1, 1))}
            className="h-9 w-9 border-gray-300" data-testid="calendar-prev-month">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <h3 className="text-xl font-semibold tracking-tight text-gray-900 min-w-[200px] text-center">
            {MONTHS_FR[currentMonth.getMonth()]} {currentMonth.getFullYear()}
          </h3>
          <Button variant="outline" size="icon" onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth()+1, 1))}
            className="h-9 w-9 border-gray-300" data-testid="calendar-next-month">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
        <Button variant="outline" size="sm" onClick={handleExportCSV} disabled={!data?.slots?.length}
          className="border-gray-300 gap-1.5" data-testid="export-calendar-btn">
          <Download className="w-4 h-4" /> Export CSV
        </Button>
      </div>

      {/* KPIs */}
      {data?.kpis && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <KPICard label="Jours ouvrés" value={data.kpis.workingDays} />
          <KPICard label="Présences" value={data.kpis.present} color="emerald" />
          <KPICard label="Absences" value={data.kpis.absent} color="red" />
          <KPICard label="Justifiées" value={data.kpis.justified} color="amber" />
          <KPICard label="Taux présence" value={`${data.kpis.presenceRate}%`}
            color={data.kpis.presenceRate >= 80 ? 'emerald' : data.kpis.presenceRate >= 50 ? 'amber' : 'red'} />
        </div>
      )}

      {/* Per-slot KPIs */}
      {data?.slots?.some(s => s.kpis) && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4" data-testid="per-slot-kpis">
          <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-[0.15em] mb-3">Détail par poste</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {data.slots.map(slot => {
              const k = slot.kpis || {};
              return (
                <div key={slot.slotId} className="flex items-center justify-between bg-white border border-gray-200 px-4 py-3 rounded">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{slot.label}</p>
                    <p className="text-xs text-gray-400 font-mono">{(slot.vehicles || []).join(', ')}</p>
                  </div>
                  <div className="flex items-center gap-3 text-right">
                    <p className="text-xs text-gray-500">{k.present}/{k.expected} jours</p>
                    <span className={cn('text-lg font-bold font-mono',
                      k.presenceRate >= 80 ? 'text-emerald-600' : k.presenceRate >= 50 ? 'text-amber-600' : 'text-red-600'
                    )}>{k.presenceRate}%</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-2">
        {Object.entries(STATUS_CONFIG).map(([key, cfg]) => {
          const Icon = cfg.icon;
          return (
            <div key={key} className={cn('inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium border rounded', cfg.cellClass)}>
              <Icon className={cn('w-3.5 h-3.5', cfg.iconColor)} /> {cfg.label}
            </div>
          );
        })}
        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium border bg-blue-50 border-blue-400 text-blue-700 rounded">
          <UserCheck className="w-3.5 h-3.5" /> Manuel
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          <span className="ml-3 text-sm text-gray-500">Chargement...</span>
        </div>
      ) : error ? (
        <Card className="border-red-200"><CardContent className="py-8 text-center">
          <p className="text-red-600 text-sm">{error}</p>
          <Button variant="outline" size="sm" className="mt-3" onClick={fetchAttendance}>Réessayer</Button>
        </CardContent></Card>
      ) : data?.slots?.length ? (
        <div className="overflow-x-auto border border-gray-200 bg-white relative" data-testid="attendance-grid">
          <table className="border-collapse text-left text-sm" style={{ minWidth: `${200 + data.numDays * 48}px` }}>
            <thead>
              <tr>
                <th className="sticky left-0 z-20 bg-gray-50 border-b border-r-2 border-gray-300 px-4 py-3 text-[10px] font-semibold text-gray-500 uppercase tracking-[0.15em]"
                  style={{ width: '200px', minWidth: '200px', boxShadow: '2px 0 5px rgba(0,0,0,0.05)' }}>
                  Poste / Véhicule
                </th>
                {Array.from({ length: data.numDays }, (_, i) => {
                  const dayNum = i + 1;
                  const dateStr = `${monthStr}-${String(dayNum).padStart(2, '0')}`;
                  const dow = getDayOfWeek(dateStr);
                  const isWeekend = dow === 0 || dow === 6;
                  const isHoliday = data.holidays?.some(h => h.date === dateStr);
                  return (
                    <th key={dayNum} className={cn('border-b border-r border-gray-200 p-1 text-center',
                        isHoliday ? 'bg-orange-50' : isWeekend ? 'bg-gray-100' : 'bg-gray-50')}
                      style={{ width: '48px', minWidth: '48px' }}>
                      <div className="text-[10px] font-mono text-gray-400 leading-none">{DAYS_SHORT[dow === 0 ? 6 : dow - 1]}</div>
                      <div className={cn('text-xs font-mono leading-none mt-0.5 font-medium',
                        isHoliday ? 'text-orange-600' : isWeekend ? 'text-gray-400' : 'text-gray-700')}>{dayNum}</div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {data.slots.map((slot) => (
                <tr key={slot.slotId}>
                  <td className="sticky left-0 z-20 bg-white border-r-2 border-b border-gray-300 px-4 py-2"
                    style={{ width: '200px', minWidth: '200px', boxShadow: '2px 0 5px rgba(0,0,0,0.05)' }}>
                    <span className="text-sm font-semibold text-gray-900 truncate block">{slot.label}</span>
                    <span className="text-[10px] text-gray-400 font-mono truncate block">{(slot.vehicles || []).join(', ') || 'Non assigné'}</span>
                  </td>
                  {slot.days.map((day) => {
                    const cfg = STATUS_CONFIG[day.status];
                    if (!cfg) return <td key={day.date} className="border-r border-b border-gray-200 p-0" style={{ width: '48px', height: '48px' }} />;
                    const Icon = cfg.icon;
                    const isManual = day.manual;
                    const isRest = day.status === 'rest';
                    return (
                      <td key={day.date} className="border-r border-b border-gray-200 p-0" style={{ width: '48px', height: '48px' }}>
                        <div className={cn('relative w-full h-full flex flex-col items-center justify-center',
                            cfg.cellClass, isRest && 'opacity-60', isManual && 'bg-blue-50 border-blue-400')}
                          style={isRest ? { background: 'repeating-linear-gradient(45deg, transparent, transparent 4px, #E5E7EB 4px, #E5E7EB 6px)' } : undefined}
                          title={`${day.date}: ${cfg.label}${day.vehicle ? ` (${day.vehicle})` : ''}`}>
                          {isManual ? <UserCheck className="w-4 h-4 text-blue-500" /> : <Icon className={cn('w-4 h-4', cfg.iconColor)} />}
                          {day.status === 'present' && day.runsCompleted != null && !day.manual && (
                            <span className="absolute top-0.5 right-0.5 bg-white/90 px-1 py-px text-[9px] font-bold font-mono text-emerald-700 border border-emerald-200 rounded shadow-sm leading-none">
                              {day.runsCompleted}/{day.runsExpected || '?'}
                            </span>
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <Card className="border-gray-200"><CardContent className="py-12 text-center">
          <CalendarClock className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500">Aucune donnée de présence pour ce mois.</p>
        </CardContent></Card>
      )}
    </div>
  );
};

const KPICard = ({ label, value, color }) => {
  const colorMap = { emerald: 'text-emerald-600', red: 'text-red-600', amber: 'text-amber-600' };
  return (
    <div className="bg-white border border-gray-200 p-4 flex flex-col gap-1">
      <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-[0.15em]">{label}</span>
      <span className={cn('text-2xl font-bold tracking-tight font-mono', colorMap[color] || 'text-gray-900')}>{value}</span>
    </div>
  );
};

export default ClientAttendanceCalendar;
